import Clarity from '@microsoft/clarity';
import { User } from '@/types';
import { TRACKING_ENABLED } from '@services/environment';
import pkg from '../../package.json';
import { calculateAccountAge } from '@utils/validationUtils';

// Extend window type for Clarity
declare global {
  interface Window {
    clarity?: (...args: unknown[]) => void;
  }
}

const CLARITY_PROJECT_ID = 's49do4p316';

interface ClarityEventData {
  [key: string]: string | number | boolean;
}

// Get the environment name for tagging
const getEnvironment = (): string => {
  return TRACKING_ENABLED ? 'production' : 'development';
};

class ClarityService {
  private initialized = false;
  private currentUser: User | null = null;
  private environment = getEnvironment();

  /**
   * Initialize Clarity tracking
   */
  public init(): void {
    if (this.initialized) {
      return;
    }

    // Only initialize when tracking is enabled
    if (!TRACKING_ENABLED) {
      this.initialized = true;
      return;
    }

    try {
      // Check if Clarity is available
      if (!Clarity || typeof Clarity.init !== 'function') {
        this.initialized = true; // Mark as initialized to prevent retries
        return;
      }

      Clarity.init(CLARITY_PROJECT_ID);
      this.initialized = true;

      // Set environment tags immediately
      this.setTag('environment', this.environment);
      this.setTag('app_version', pkg.version);
      this.setTag('timestamp', new Date().toISOString());
    } catch (error) {
      console.error('Failed to initialize Clarity:', error);
      this.initialized = true; // Mark as initialized to prevent retries
    }
  }

  /**
   * Identify a user for tracking
   */
  public identifyUser(user: User): void {
    this.currentUser = user;

    const friendlyName = user.firstName
      ? `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}`
      : user.email.split('@')[0];

    if (!this.initialized || !this.isClarityAvailable()) {
      return;
    }

    try {
      Clarity.identify(user.id, undefined, undefined, friendlyName);
      this.setUserTags(user);
      this.trackEvent('user_identified', {
        user_id: user.id,
        has_first_name: !!user.firstName,
        has_last_name: !!user.lastName,
        has_phone: !!user.phoneNumber,
        onboarding_completed: user.onboardingCompleted,
      });
    } catch (error) {
      console.error('Failed to identify user in Clarity:', error);
    }
  }

  /**
   * Clear user identification (e.g., on logout)
   */
  public clearUser(): void {
    this.currentUser = null;

    if (!this.initialized || !this.isClarityAvailable()) {
      return;
    }

    try {
      this.setTag('user_type', 'anonymous');
      this.setTag('user_id', '');
      this.setTag('user_email', '');
      this.setTag('onboarding_status', 'unknown');
      this.trackEvent('user_logged_out');
    } catch (error) {
      console.error('Failed to clear user in Clarity:', error);
    }
  }

  /**
   * Track a custom event
   */
  public trackEvent(eventName: string, data?: ClarityEventData): void {
    if (!this.initialized || !this.isClarityAvailable()) {
      return;
    }

    try {
      Clarity.event(eventName);
      if (data) {
        Object.entries(data).forEach(([key, value]) => {
          this.setTag(`event_${key}`, String(value));
        });
      }
    } catch (error) {
      console.error('Failed to track event in Clarity:', error);
    }
  }

  /**
   * Set a custom tag
   */
  public setTag(key: string, value: string | string[]): void {
    if (!this.initialized || !this.isClarityAvailable()) {
      return;
    }

    try {
      Clarity.setTag(key, value);
    } catch (error) {
      console.error('Failed to set tag in Clarity:', error);
    }
  }

  /**
   * Upgrade the current session with a reason
   */
  public upgradeSession(reason: string): void {
    if (!this.initialized || !this.isClarityAvailable()) {
      return;
    }

    try {
      Clarity.upgrade(reason);
    } catch (error) {
      console.error('Failed to upgrade session in Clarity:', error);
    }
  }

  /**
   * Set cookie consent
   */
  public setConsent(hasConsent: boolean = true): void {
    if (!this.initialized || !this.isClarityAvailable()) {
      return;
    }

    try {
      Clarity.consent(hasConsent);
    } catch (error) {
      console.error('Failed to set consent in Clarity:', error);
    }
  }

  /**
   * Track page view with context
   */
  public trackPageView(
    pageName: string,
    additionalContext?: ClarityEventData
  ): void {
    if (!this.initialized || !this.isClarityAvailable()) {
      return;
    }

    try {
      // Re-identify user on each page to ensure tracking continuity
      if (this.currentUser) {
        Clarity.identify(this.currentUser.id);
      }

      this.setTag('current_page', pageName);
      this.setTag('page_timestamp', new Date().toISOString());
      this.trackEvent('page_view', {
        page_name: pageName,
        ...additionalContext,
      });
    } catch (error) {
      console.error('Failed to track page view in Clarity:', error);
    }
  }

  /**
   * Track onboarding progress
   */
  public trackOnboardingStep(step: number, stepName: string): void {
    this.trackEvent('onboarding_step', {
      step_number: step,
      step_name: stepName,
    });
    this.setTag('onboarding_current_step', step.toString());
    this.setTag('onboarding_step_name', stepName);
  }

  /**
   * Track investment-related actions
   */
  public trackInvestmentAction(
    action: string,
    investmentId?: string,
    amount?: number
  ): void {
    const eventData: ClarityEventData = { action };
    if (investmentId) eventData.investment_id = investmentId;
    if (amount) eventData.amount = amount;

    this.trackEvent('investment_action', eventData);
    this.upgradeSession(`investment_${action}`);
  }

  /**
   * Track errors for debugging
   */
  public trackError(error: Error, context?: string): void {
    this.trackEvent('error_occurred', {
      error_message: error.message,
      error_name: error.name,
      context: context || 'unknown',
      stack_available: !!error.stack,
    });
    this.upgradeSession('error_occurred');
  }

  /**
   * Set user-related tags
   */
  private setUserTags(user: User): void {
    this.setTag('user_type', 'authenticated');
    this.setTag('user_id', user.id);
    this.setTag('user_email', user.email);
    this.setTag(
      'onboarding_status',
      user.onboardingCompleted ? 'completed' : 'incomplete'
    );

    if (user.firstName) this.setTag('has_first_name', 'true');
    if (user.lastName) this.setTag('has_last_name', 'true');
    if (user.phoneNumber) this.setTag('has_phone_number', 'true');

    if (user.createdAt) {
      const accountAge = calculateAccountAge(user.createdAt);
      this.setTag('account_age_days', accountAge.toString());
    }
  }

  /**
   * Check if Clarity is available and ready
   */
  private isClarityAvailable(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof window.clarity === 'function' &&
      Clarity &&
      typeof Clarity.init === 'function'
    );
  }

  /**
   * Check if Clarity is ready for tracking
   */
  public isReady(): boolean {
    return this.initialized;
  }

  /**
   * Get current environment
   */
  public getEnvironment(): string {
    return this.environment;
  }
}

// Export singleton instance
export const clarityService = new ClarityService();

// Export the class for testing
export { ClarityService };

// Export convenience functions with proper binding
export const initClarity = () => clarityService.init();
export const identifyUserInClarity = (user: User) =>
  clarityService.identifyUser(user);
export const clearUserFromClarity = () => clarityService.clearUser();
export const trackClarityEvent = (eventName: string, data?: ClarityEventData) =>
  clarityService.trackEvent(eventName, data);
export const setClarityTag = (key: string, value: string | string[]) =>
  clarityService.setTag(key, value);
export const upgradeClaritySession = (reason: string) =>
  clarityService.upgradeSession(reason);
export const setClarityConsent = (hasConsent?: boolean) =>
  clarityService.setConsent(hasConsent);
export const trackClarityPageView = (
  pageName: string,
  additionalContext?: ClarityEventData
) => clarityService.trackPageView(pageName, additionalContext);
export const trackClarityOnboardingStep = (step: number, stepName: string) =>
  clarityService.trackOnboardingStep(step, stepName);
export const trackClarityInvestmentAction = (
  action: string,
  investmentId?: string,
  amount?: number
) => clarityService.trackInvestmentAction(action, investmentId, amount);
export const trackClarityError = (error: Error, context?: string) =>
  clarityService.trackError(error, context);
